// MessageUtils.js
import React, { useState, useRef, useEffect } from 'react';
import Message from '../Message';
import bootbox from 'bootbox';
import ReactDOM from 'react-dom';

export const useMessage = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messageType, setMessageType] = useState('default');

  const openMessage = (type) => {
    setMessageType(type);
    setIsOpen(true);
  };
  const closeMessage = () => setIsOpen(false);

  const MessageComponent = ({ children }) => (
    <Message isOpen={isOpen} onClose={closeMessage} type={messageType}>
      {children}
    </Message>
  );

  return { openMessage, closeMessage, MessageComponent };
};

export function forceGlobalBlur() {
  const el = document.activeElement;
  if (!el) return;

  try {
    el.blur();   // normal blur
  } catch (e) {}

  // brutal but effective fallback:
  document.body.focus();
}

export const showBootboxModal = (message,
  title,
  btnOk = "Ok",
  btnCancel = "",
  callback = () => { },
  callbackCancel = () => { }) => {
    requestAnimationFrame(() => {
      forceGlobalBlur();

      if (!message || !title) {
        return;
      }
      const formattedMessage = message.replace(/\n/g, '<br>');

      let options = {
        message: formattedMessage,
        title: title,
        buttons: {
          confirm: {
            label: btnOk,
            className: 'btn-primary',
          },
          ok: {
            label: btnOk,
            className: 'btn-primary',
          },
          cancel: {
            label: btnCancel,
            className: 'btn-secondary',
          },
        },
      };

      if (!btnCancel) {
        delete options.buttons.cancel;
        delete options.buttons.confirm;
        bootbox.alert({
          ...options,
          callback: (result) => {
            callback(); // Call the callback if OK is clicked
          },
        });
      }
      else {
        delete options.buttons.ok;
        bootbox.confirm({
          ...options,
          callback: (result) => {
            if (result) {
              callback(); // Call the callback if OK is clicked
            }
            else {
              callbackCancel(); // Call the callback if Cancel is clicked
            }
          },
        });
      }
    });
};

export const ReactBootboxModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  children,
  confirmLabel = 'OK',
  cancelLabel = 'Annuler',
  deleteLabel = 'Supprimer',
  showCancel = false,
  showDelete = false,
  onDelete = () => { }
}) => {
  const modalRef = useRef(null);
  const contentRef = useRef(document.createElement('div'));

  useEffect(() => {
    if (isOpen) {
      const buttons = {};

      if (showCancel) {
        buttons.cancel = {
          label: cancelLabel,
          className: 'btn-secondary',
          callback: onClose
        };
      }
      if (showDelete) {
        buttons.delete = {
          label: deleteLabel,
          className: 'btn-danger',
          callback: onDelete
        };
      }

      buttons.confirm = {
        label: confirmLabel,
        className: 'btn-primary',
        callback: onConfirm
      };

      modalRef.current = bootbox.dialog({
        title,
        message: contentRef.current,
        buttons,
        onEscape: onClose,
        closeButton: false
      });

      // Intégration du composant React
      ReactDOM.render(
        <ModalContext.Provider value={{ closeModal: onClose }}>
          {children}
        </ModalContext.Provider>,
        contentRef.current
      );
    }

    return () => {
      if (modalRef.current) {
        modalRef.current.modal('hide');
        ReactDOM.unmountComponentAtNode(contentRef.current);
      }
    };
  }, [isOpen]);

  return null;
};

// Context pour la gestion des fermetures
const ModalContext = React.createContext({});

// Hook personnalisé
export const useModal = () => {
  const context = React.useContext(ModalContext);
  return context;
};

export const formatDateToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}${month}${day}`;
  };

  export const formatDateTimeToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}${month}${day}${hours}${minutes}`;
  };

  export const formatDateTimeSecondToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}${month}${day}${hours}${minutes}${seconds}`;
  };
  
  export const formatDateToStringHistoric = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${day}/${month}/${year} - ${hours}h${minutes}`;
  };